import { List } from 'immutable';
import invert from 'lodash/invert';
import isEmpty from 'lodash/isEmpty';
import { getValues } from 'redux-form';

import * as PaymentsConstants from './constants';
import * as D from './deps';
import * as PaymentsUtils from './utils';

/**
 * A handy utility function for selectors to use in order to get properties from a map
 *
 * @param {string | Array} arg
 * @param {*} [defaultResult]
 * @returns {function(*)}
 */
export function getPropFromMap(arg, defaultResult) {
  return map => {
    return map
      ? Array.isArray(arg)
        ? map.getIn(arg, defaultResult)
        : map.get(arg, defaultResult)
      : defaultResult;
  };
}

/**
 * A handy utility function for selectors.
 *
 * If more than one arg is passed in, the last one is used as the expected value
 * all the others are used in the getter chain
 *
 * @param {*} args
 * @returns {function(*): boolean}
 */
export function isEqual(...args) {
  const numArgs = args.length;
  const expectedValue = args[numArgs - 1];

  return value => {
    const actualValue = numArgs > 1 ? !!value && value.getIn(args.slice(0, -1)) : value;

    return actualValue === expectedValue;
  };
}

/**
 * A handy utility function for selectors.
 *
 * If more than one arg is passed in, the last one is used as the expected value
 * all the others are used in the getter chain
 *
 * @param {*} args
 * @returns {function(*): boolean}
 */
export function isNotEqual(...args) {
  const numArgs = args.length;
  const expectedValue = args[numArgs - 1];

  return value => {
    const actualValue = numArgs > 1 ? !!value && value.getIn(args.slice(0, -1)) : value;

    return actualValue !== expectedValue;
  };
}

/**
 * A handy utility function for selectors.
 *
 * If more than one arg is passed in, the last one is used as the value to check
 * all the others are used in the getter chain
 *
 * @param {*} [args]
 * @returns {function(*): boolean}
 */
export function isNotEmpty(...args) {
  return map => {
    const target = args.length ? map.getIn(args) : map;

    return !!target && !target.isEmpty();
  };
}

/**
 * To be used as a custom "resolver" when memoizing a function that has multiple arguments
 * See: https://github.com/lodash/lodash/issues/2115
 *
 * @param {*} args
 * @returns {string}
 */
export function generateMemoKey(...args) {
  return JSON.stringify(args);
}

function filterOffersByPaymentProvider(productGroups, paymentProvider) {
  return productGroups.filter(productGroup => {
    return productGroup.get(PaymentsConstants.ProductKeys.PAYMENT_PROVIDER) === paymentProvider;
  });
}

export function findOffer(productGroups, filters) {
  const { paymentProvider, ...backingProductFilters } = filters;

  // @todo [RADIO-27422] The paymentProvider that the backend provides on the product object is not always accurate
  // Use the paymentProvider on the productGroup instead
  const offers = paymentProvider
    ? filterOffersByPaymentProvider(productGroups, paymentProvider)
    : productGroups;

  return offers.find(productGroup => {
    if (isEmpty(backingProductFilters)) {
      return true;
    }

    const backingProducts = productGroup.get(PaymentsConstants.ProductKeys.BACKING_PRODUCTS);
    if (!backingProducts) {
      return;
    }

    return backingProducts.some(backingProduct => {
      return Object.keys(backingProductFilters).every(prop => {
        return backingProduct.get(prop) === backingProductFilters[prop];
      });
    });
  });
}

export function hasOffer(productGroups, filters) {
  return !!findOffer(productGroups, filters);
}

export function findBackingProduct(productGroups, filters) {
  const { paymentProvider, ...backingProductFilters } = filters;

  // @todo [RADIO-27422] The paymentProvider that the backend provides on the product object is not always accurate
  // Use the paymentProvider on the productGroup instead
  return getBackingProducts(productGroups, paymentProvider).find(backingProduct => {
    return Object.keys(backingProductFilters).every(prop => {
      return backingProduct.get(prop) === backingProductFilters[prop];
    });
  });
}

export function findInitialProduct(productGroups, tier, discountType) {
  const productGroup = productGroups.find(group => {
    return PaymentsUtils.hasInitialProduct(group, tier, discountType);
  });

  if (productGroup) {
    return productGroup.get(PaymentsConstants.ProductKeys.INITIAL_PRODUCT);
  }
}

export function getOfferProp(
  propName,
  productGroups,
  tier,
  discountType,
  durationType,
  checkInitialProduct = true,
  checkBackingProducts = true,
) {
  if (!productGroups) {
    return;
  }
  const initialProduct =
    !!checkInitialProduct && findInitialProduct(productGroups, tier, discountType);
  if (initialProduct) {
    return initialProduct.get(propName);
  }

  if (!checkBackingProducts) {
    return;
  }

  const filters = {
    [PaymentsConstants.ProductKeys.PRODUCT_TIER]: tier,
    [PaymentsConstants.ProductKeys.DISCOUNT_TYPE]: discountType,
  };

  if (durationType) {
    filters[PaymentsConstants.ProductKeys.DURATION_TYPE] = durationType;
  }

  const backingProduct = findBackingProduct(productGroups, filters);
  if (backingProduct) {
    return backingProduct.get(propName);
  }
}

export function getBackingProductProp(propName, productGroups, tier, discountType, durationType) {
  return getOfferProp(propName, productGroups, tier, discountType, durationType, false, true);
}

export function getInitialProductProp(propName, productGroups, tier, discountType, durationType) {
  return getOfferProp(propName, productGroups, tier, discountType, durationType, true, false);
}

export function getCreditCardFormValues(creditCardFormState) {
  if (!creditCardFormState) {
    return null;
  }

  return getValues(creditCardFormState);
}

export function getUpgradeOfferStatsFields(userSettings) {
  return {
    sourceId: userSettings.get('sourceId'),
    sourceType: userSettings.get('sourceType'),
  };
}

export function isCreditCardDeleted(paymentProvider, isCreditCardOnFile) {
  return paymentProvider === PaymentsConstants.PaymentProviders.CREDIT_CARD && !isCreditCardOnFile;
}

export function hasPaymentInfoOnFile(paymentProviderIsPaypal, hasCcOnFile) {
  return paymentProviderIsPaypal || hasCcOnFile;
}

export function isNonPaymentBackedUser(isGiftee, isInPaymentBackedTrial) {
  // TODO: WEBCLIENT-8083
  return isGiftee && !isInPaymentBackedTrial;
}

export function getInitialProductsFromProductGroups(productGroups) {
  return productGroups.reduce((initialProducts, productGroup) => {
    if (productGroup) {
      const initialProduct = productGroup.get(PaymentsConstants.ProductKeys.INITIAL_PRODUCT);
      if (initialProduct) {
        return initialProducts.push(initialProduct);
      }
    }
    return initialProducts;
  }, List());
}

export function isCurrentListenerCanceledSubscriber(autoRenew, isSubscriber, inPaymentBackedTrial) {
  return autoRenew === false && (isSubscriber === true || inPaymentBackedTrial === true);
}

export function hasUserDowngraded(activeProductTier, currentProductTier) {
  if (!currentProductTier) {
    return false;
  }

  return PaymentsUtils.isDowngrade(activeProductTier, currentProductTier);
}

export function isActiveProductOnceBilledTrial(isInPaymentBackedTrial, price, timesRenewable) {
  return isInPaymentBackedTrial && price !== 0 && timesRenewable === 0;
}

export function isIn6MonthPremiumNACTrial(
  tier,
  discountType,
  isInPaymentBackedTrial,
  timesRenewable,
  durationType,
) {
  if (durationType === PaymentsConstants.DurationTypes.MONTHLY) {
    return (
      isInPaymentBackedTrial &&
      tier === PaymentsConstants.tiers.PANDORA_PREMIUM &&
      !PaymentsUtils.isDiscount(discountType) &&
      timesRenewable === 5
    );
  }
  if (durationType === PaymentsConstants.DurationTypes.YEARLY) {
    return (
      isInPaymentBackedTrial &&
      tier === PaymentsConstants.tiers.PANDORA_PREMIUM &&
      !PaymentsUtils.isDiscount(discountType) &&
      timesRenewable === 0
    );
  }
}

export function getOfferedProductGroups(specialOfferProductGroups, availableProducts) {
  if (specialOfferProductGroups && !specialOfferProductGroups.isEmpty()) {
    return specialOfferProductGroups;
  }

  if (availableProducts && !availableProducts.isEmpty()) {
    return availableProducts;
  }

  return List();
}

export function isTrialEligible(productGroups) {
  if (!productGroups) {
    return false;
  }

  return productGroups.some(group => {
    const initialProduct = group.get(PaymentsConstants.ProductKeys.INITIAL_PRODUCT);
    return (
      initialProduct &&
      initialProduct.get(PaymentsConstants.ProductKeys.PRODUCT_TYPE) === 'TRIAL_SUBSCRIPTION'
    );
  });
}

export function isInPlusTrial(productTierIsPlus, isInPaymentBackedTrial) {
  return productTierIsPlus && isInPaymentBackedTrial;
}

export function isAgeIneligible(ccFormStatus, listenerBirthYear) {
  const isAgeIneligibleUpgradeSubmit =
    ccFormStatus === PaymentsConstants.ErrorCodes.FAMILY_PLAN_AGE_INELIGIBLE;
  const isAgeIneligibleUpgradeAttempt = D.Utils.DateUtils.isUnderageFamilyPlan(listenerBirthYear);
  return isAgeIneligibleUpgradeSubmit || isAgeIneligibleUpgradeAttempt;
}

export function isSubscriptionSoonToExpire(
  nextBillingDate,
  hasAutoRenew,
  isComplimentary,
  isGiftee,
) {
  if (hasAutoRenew || isComplimentary || isGiftee) {
    return false;
  }

  return PaymentsUtils.getDaysUntilNextBillingDate(nextBillingDate) < 9;
}

// @todo [WEBCLIENT-7087] We won't need this paymentProvider check once the backend no longer sends different paymentProvider options
function productGroupHasMatchingPaymentProvider(productGroup, paymentProvider) {
  if (!paymentProvider) {
    return true;
  }

  return (
    paymentProvider === PaymentsConstants.PaymentProviders.NONE ||
    productGroup.get(PaymentsConstants.ProductKeys.PAYMENT_PROVIDER) === paymentProvider
  );
}

export function getBackingProducts(productGroups, paymentProvider) {
  return productGroups.reduce((backingProducts, productGroup) => {
    const backingProductOptions = productGroup.get(PaymentsConstants.ProductKeys.BACKING_PRODUCTS);

    if (
      backingProductOptions &&
      !backingProductOptions.isEmpty() &&
      productGroupHasMatchingPaymentProvider(productGroup, paymentProvider)
    ) {
      return backingProducts.concat(backingProductOptions);
    }

    return backingProducts;
  }, List());
}

export function hasDiscountOffer(productGroups, isGiftee, discountType) {
  // The backend does not give us stu/mil options if the listener is on a GIFT
  // as a result we hard code to always have the offers.
  // We can do this because we eventually show them a "You are not eligible" message anyway
  if (isGiftee) {
    return true;
  }

  return !!findBackingProduct(productGroups, {
    [PaymentsConstants.ProductKeys.DISCOUNT_TYPE]: discountType,
  });
}

export function findPlanByTierAndDiscountType(array, tier, discountType) {
  return array.find(item => {
    return item && item.tier === tier && item.discountType === discountType;
  });
}

export function findBillingOption(billingOptions, durationType) {
  return billingOptions.find(billingOption => billingOption.durationType === durationType);
}

export function createPlanData(tier, discountType, durationType, price, currency) {
  return {
    tier,
    discountType,
    billingOptions: [
      {
        durationType,
        price,
        currency,
      },
    ],
  };
}

export function parseBackingProductsToAvailablePlans(availablePlans, backingProduct) {
  const tier = backingProduct.get(PaymentsConstants.ProductKeys.PRODUCT_TIER);
  const discountType = backingProduct.get(PaymentsConstants.ProductKeys.DISCOUNT_TYPE);
  const price = backingProduct.get(PaymentsConstants.ProductKeys.PRICE);
  const durationType = backingProduct.get(PaymentsConstants.ProductKeys.DURATION_TYPE);
  const currency = backingProduct.get(PaymentsConstants.ProductKeys.ACCEPTED_CURRENCY);
  const availablePlan = findPlanByTierAndDiscountType(availablePlans, tier, discountType);

  if (availablePlan) {
    const billingOption = findBillingOption(availablePlan.billingOptions, durationType);
    if (!billingOption) {
      availablePlan.billingOptions.push({
        durationType,
        price,
        currency,
      });
    }
  } else {
    availablePlans.push(createPlanData(tier, discountType, durationType, price, currency));
  }

  return availablePlans;
}

export function sortPlans(planA, planB) {
  if (planA.tier === planB.tier) {
    return planA.discountType === PaymentsConstants.discountTypes.NONE ? -1 : 1;
  }

  return PaymentsUtils.compareTiers(planB.tier, planA.tier);
}

export function sortBillingOptions(plan) {
  plan.billingOptions.sort((optionA, optionB) => {
    return (
      PaymentsConstants.DurationTypeOrder.indexOf(optionA.durationType) -
      PaymentsConstants.DurationTypeOrder.indexOf(optionB.durationType)
    );
  });

  return plan;
}

/**
 * An AvailablePlan is what we use on the subscription management page to
 * determine what plans we show to a listener.
 *
 * Eventually, this doc / meta information will find its way into code
 * in the form of a Record when we revisit the data model - at that point we should remove this doc block.
 *
 * @typedef {object} AvailablePlan
 * @property {string} tier
 * @property {string} discountType
 * @property {BillingOption} billingOptions
 */

/**
 * @typedef {object} BillingOption
 * @property {string} price
 * @property {string} durationType
 */

/**
 *
 * @param {Array} productGroups - An array of backing products
 * @param {string} paymentProvider
 * @returns {Array.<AvailablePlan>} A sorted array of availablePlans
 */
export function getAvailablePlans(productGroups, paymentProvider) {
  // @todo [WEBCLIENT-7277] get rid of `getAvailablePlans()` - replace with standard selectors
  const backingProducts = getBackingProducts(productGroups, paymentProvider);
  return backingProducts
    .reduce(parseBackingProductsToAvailablePlans, [])
    .sort(sortPlans)
    .map(sortBillingOptions);
}

export function getHasSeenPlanDetails(isOnPaymentsPage, isOnVerificationPage) {
  return isOnPaymentsPage || isOnVerificationPage;
}

export function isUserOfferedLegacyPricing(backingProduct) {
  return backingProduct
    ? PaymentsUtils.getBackingProductPrice(backingProduct) === PaymentsConstants.LEGACY_PRICING
    : false;
}

export function filterProductGroupsByPaymentProvider(productGroups, paymentProvider) {
  return productGroups.filter(productGroup =>
    productGroupHasMatchingPaymentProvider(productGroup, paymentProvider),
  );
}

export function getActiveProductRetailPrice(
  productGroups,
  tier,
  discountType,
  productType,
  durationType,
  price,
  paymentProvider,
) {
  // @todo [RADIO-27023] We have to peek inside of offered products until the backend is updated
  if (productType === PaymentsConstants.productTypes.TRIAL_SUBSCRIPTION) {
    // @todo [WEBCLIENT-7087] Filter out products with a different payment provider.  This can be removed when backend task is complete
    const filteredProductGroups = filterProductGroupsByPaymentProvider(
      productGroups,
      paymentProvider,
    );
    return getBackingProductPrice(filteredProductGroups, tier, discountType, durationType);
  }

  return price;
}

export function getUndiscountedPrice(productGroups, tier, durationType) {
  const undiscountedPrice = getBackingProductPrice(
    productGroups,
    tier,
    PaymentsConstants.discountTypes.NONE,
    durationType,
  );
  // WEBCLIENT-9908 - take out default (hard-coded) prices when undiscounted plan data is available from the server
  return undiscountedPrice
    ? undiscountedPrice
    : PaymentsUtils.getDefaultPrice({
        tier,
        discountType: PaymentsConstants.discountTypes.NONE,
        durationType,
      });
}

export function getCurrentProduct(activeProduct, pendingSubscriptionProduct) {
  return pendingSubscriptionProduct || activeProduct;
}

export function getPendingSubscriptionProduct(pendingProducts) {
  if (!pendingProducts) {
    return;
  }

  return pendingProducts.find(product => {
    return (
      product.get(PaymentsConstants.ProductKeys.PRODUCT_TYPE) ===
      PaymentsConstants.SubscriptionKeys.SUBSCRIPTION
    );
  });
}

export function getPendingTrial(pendingProducts) {
  if (!pendingProducts) {
    return;
  }

  return pendingProducts.find(product => {
    return (
      product.get(PaymentsConstants.ProductKeys.PRODUCT_TYPE) ===
      PaymentsConstants.SubscriptionKeys.PAYMENT_BACKED_CODE
    );
  });
}

export function getPendingGifts(pendingProducts) {
  if (!pendingProducts) {
    return;
  }

  return pendingProducts.toArray().filter(product => {
    return (
      product.get(PaymentsConstants.ProductKeys.PRODUCT_TYPE) ===
      PaymentsConstants.productTypes.NON_PAYMENT_BACKED_CODE
    );
  });
}

export function pendingGiftsOnDifferentTier(pendingGifts, tier) {
  if (!pendingGifts) {
    return;
  }

  return pendingGifts.filter(pendingGift => {
    return pendingGift.get(PaymentsConstants.ProductKeys.PRODUCT_TIER) !== tier;
  });
}

export function getSubscriptionProduct(activeProduct, pendingSubscriptionProduct) {
  if (
    activeProduct &&
    activeProduct.get(PaymentsConstants.ProductKeys.PRODUCT_TYPE) ===
      PaymentsConstants.productTypes.SUBSCRIPTION
  ) {
    return activeProduct;
  }

  return pendingSubscriptionProduct;
}

export function is6MPremiumTrialExpiringWithActiveProduct(
  nextBillingDate,
  trialEndDate,
  activeProductDurationType,
) {
  return (
    !!trialEndDate &&
    nextBillingDate === trialEndDate &&
    activeProductDurationType === PaymentsConstants.DurationTypes.YEARLY
  );
}

export function is6MPremiumTrialExpiringAfterActiveProduct(
  nextBillingDate,
  trialEndDate,
  activeProductDurationType,
) {
  return (
    !!trialEndDate &&
    nextBillingDate !== trialEndDate &&
    activeProductDurationType === PaymentsConstants.DurationTypes.YEARLY
  );
}

export function getActiveProductExpirationDate(nextBillingDate, trialEndDate) {
  return trialEndDate ? trialEndDate : nextBillingDate;
}

export const getOffersToDisplayArray = (tier, discountType, isOnPlansPage) => {
  let offersToDisplay = [
    {
      tier: PaymentsConstants.tiers.PANDORA_PLUS,
      discountType: PaymentsConstants.discountTypes.NONE,
    },
    {
      tier: PaymentsConstants.tiers.PANDORA_PREMIUM,
      discountType: PaymentsConstants.discountTypes.NONE,
    },
  ];

  if (isOnPlansPage) {
    offersToDisplay.unshift({
      tier: PaymentsConstants.tiers.AD_SUPPORTED,
      discountType: PaymentsConstants.discountTypes.NONE,
    });
  }
  if (tier && tier !== PaymentsConstants.tiers.AD_SUPPORTED) {
    offersToDisplay = [{ tier, discountType }];
  }
  return offersToDisplay;
};

export function getOfferPrice(productGroups, tier, discountType, durationType) {
  return getOfferProp(
    PaymentsConstants.ProductKeys.PRICE,
    productGroups,
    tier,
    discountType,
    durationType,
  );
}

export function getBackingProductPrice(productGroups, tier, discountType, durationType) {
  return getBackingProductProp(
    PaymentsConstants.ProductKeys.PRICE,
    productGroups,
    tier,
    discountType,
    durationType,
  );
}

export function getBackingProductId(productGroups, tier, discountType, durationType) {
  return getBackingProductProp(
    PaymentsConstants.ProductKeys.PRODUCT_ID,
    productGroups,
    tier,
    discountType,
    durationType,
  );
}

export function getOfferDurationTime(
  productGroups,
  tier,
  discountType,
  durationType,
  checkInitialProduct,
  checkingBackingProducts,
) {
  return getOfferProp(
    PaymentsConstants.ProductKeys.DURATION_TIME,
    productGroups,
    tier,
    discountType,
    durationType,
    checkInitialProduct,
    checkingBackingProducts,
  );
}

export function getOfferDurationType(
  productGroups,
  tier,
  discountType,
  durationType,
  checkInitialProduct,
  checkingBackingProducts,
) {
  return getOfferProp(
    PaymentsConstants.ProductKeys.DURATION_TYPE,
    productGroups,
    tier,
    discountType,
    durationType,
    checkInitialProduct,
    checkingBackingProducts,
  );
}

export function getOfferTimesRenewable(productGroups, tier, discountType, durationType) {
  return getInitialProductProp(
    PaymentsConstants.ProductKeys.TIMES_RENEWABLE,
    productGroups,
    tier,
    discountType,
    durationType,
  );
}

export function getVendorSku(productGroups, tier, discountType, paymentProvider) {
  const productGroup = findOffer(productGroups, {
    [PaymentsConstants.ProductKeys.PRODUCT_TIER]: tier,
    [PaymentsConstants.ProductKeys.DISCOUNT_TYPE]: discountType,
    [PaymentsConstants.ProductKeys.PAYMENT_PROVIDER]: paymentProvider,
  });

  if (productGroup) {
    return productGroup.get(PaymentsConstants.ProductKeys.VENDOR_SKU);
  }
}

export function getOfferTrialType(productGroups, tier, discountType) {
  if (
    isOfferedNoTrial(productGroups, tier, discountType) ||
    !findInitialProduct(productGroups, tier, discountType)
  ) {
    return PaymentsConstants.OfferTrialTypes.NO_TRIAL;
  } else if (isOfferedFreeTrial(productGroups, tier, discountType)) {
    return PaymentsConstants.OfferTrialTypes.FREE;
  } else if (isOffered6MonthPlusToPremiumAnnualTrial(productGroups, tier, discountType)) {
    return PaymentsConstants.OfferTrialTypes.SIX_MONTH_ANNUAL_PREMIUM;
  } else if (isOfferedOnceBilledTrial(productGroups, tier, discountType)) {
    return PaymentsConstants.OfferTrialTypes.ONCE_BILLED;
  } else if (isOfferedMonthlyBilledTrial(productGroups, tier, discountType)) {
    return PaymentsConstants.OfferTrialTypes.MONTHLY_BILLED_LIMITED;
  }
  return null;
}

export function isOffered6MonthPlusToPremiumAnnualTrial(productGroups, tier, discountType) {
  const initialProduct = findInitialProduct(productGroups, tier, discountType);
  if (initialProduct) {
    const offerDurationType = getOfferDurationType(
      productGroups,
      tier,
      discountType,
      null,
      true,
      false,
    );
    const offerDurationTime = getOfferDurationTime(
      productGroups,
      tier,
      discountType,
      null,
      true,
      false,
    );
    const offerTimesRenewable = getOfferTimesRenewable(productGroups, tier, discountType, null);
    return (
      tier === PaymentsConstants.tiers.PANDORA_PREMIUM &&
      !PaymentsUtils.isDiscount(discountType) &&
      offerDurationType === PaymentsConstants.DurationTypes.YEARLY &&
      offerDurationTime === 1 &&
      offerTimesRenewable === 0
    );
  }
  return false;
}

export function isOfferedNoTrial(productGroups, tier, discountType) {
  const initialProduct = findInitialProduct(productGroups, tier, discountType);
  return !!initialProduct && initialProduct.get(PaymentsConstants.ProductKeys.DURATION_TIME) === 0;
}

export function getTrialPrice(productGroups, tier, discountType) {
  const initialProduct = findInitialProduct(productGroups, tier, discountType);
  return !!initialProduct && initialProduct.get(PaymentsConstants.ProductKeys.PRICE);
}

export function isOfferedPaidTrial(productGroups, tier, discountType) {
  const initialProduct = findInitialProduct(productGroups, tier, discountType);
  return !!initialProduct && initialProduct.get(PaymentsConstants.ProductKeys.PRICE) > 0;
}

export function isOfferedOnceBilledTrial(productGroups, tier, discountType) {
  const initialProduct = findInitialProduct(productGroups, tier, discountType);
  return (
    !!initialProduct && initialProduct.get(PaymentsConstants.ProductKeys.TIMES_RENEWABLE) === 0
  );
}

export function isOfferedFreeTrial(productGroups, tier, discountType) {
  const initialProduct = findInitialProduct(productGroups, tier, discountType);
  return !!initialProduct && initialProduct.get(PaymentsConstants.ProductKeys.PRICE) === 0;
}

export function isOfferedMonthlyBilledTrial(productGroups, tier, discountType) {
  const initialProduct = findInitialProduct(productGroups, tier, discountType);
  if (initialProduct) {
    return initialProduct.get(PaymentsConstants.ProductKeys.TIMES_RENEWABLE) > 0;
  }
  return null;
}

export function getUpgradeOfferPrice(productGroups, tier, discountType, durationType) {
  const offerPrice = getOfferPrice(productGroups, tier, discountType, durationType);
  if (offerPrice) {
    return offerPrice;
  }

  return getBackingProductPrice(productGroups, tier, discountType, durationType);
}

export function getUpgradeOfferDurationType(productGroups, tier, discountType) {
  const filters = {
    [PaymentsConstants.ProductKeys.PRODUCT_TIER]: tier,
    [PaymentsConstants.ProductKeys.DISCOUNT_TYPE]: discountType,
  };

  let backingProduct = findBackingProduct(
    productGroups,
    Object.assign({}, filters, {
      [PaymentsConstants.ProductKeys.DURATION_TYPE]: PaymentsConstants.DurationTypes.MONTHLY,
    }),
  );
  if (backingProduct) {
    return PaymentsConstants.DurationTypes.MONTHLY;
  }

  backingProduct = findBackingProduct(productGroups, filters);
  if (backingProduct) {
    return backingProduct.get(PaymentsConstants.ProductKeys.DURATION_TYPE);
  }
}

export function hasMultipleBackingProducts(productGroups, tier, discountType) {
  const filters = {
    [PaymentsConstants.ProductKeys.PRODUCT_TIER]: tier,
    [PaymentsConstants.ProductKeys.DISCOUNT_TYPE]: discountType,
  };

  const offer = findOffer(productGroups, filters);
  return !!offer && offer.get(PaymentsConstants.ProductKeys.BACKING_PRODUCTS).size > 1;
}

export function isProductTypeSpecialOffer(productType) {
  return [
    PaymentsConstants.productTypes.PAYMENT_BACKED_CODE,
    PaymentsConstants.productTypes.NON_PAYMENT_BACKED_CODE,
  ].includes(productType);
}

export function is3rdPartyBilled(isPartnerBilled, isVendorBilled) {
  return isPartnerBilled || isVendorBilled;
}

export function hasValidCreditCardOnFile(hasCreditCardOnFile, isInvalidCreditCard) {
  return hasCreditCardOnFile && !isInvalidCreditCard;
}

export function isPartnerUpgradeFlow(entitlementId) {
  return !!entitlementId;
}

export function getPartnerUpgradeFeatureCode(tier) {
  return invert(PaymentsConstants.mapPartnerUpgradeFeatureCodeToTier)[tier];
}

export function getPartnerUpgradeUrl(partnerName, featureCode, entitlementId) {
  const encodedPartnerName = encodeURIComponent(partnerName);
  return `${D.Utils.Route.getPartnerUpgradePath()}?entitlementId=${entitlementId}&store=${encodedPartnerName}&featureCode=${featureCode}`;
}

export function hasSelectedPlan(selectedTier, partnerUpgradeTier, specialOfferTier) {
  return !!(selectedTier || partnerUpgradeTier || specialOfferTier);
}

export function isProcessingPaypalToken(paypalToken, isPaypalComplete) {
  return !!paypalToken && !isPaypalComplete;
}

export function didChangePlans(
  previousTier,
  previousDiscountType,
  currentTier,
  currentDiscountType,
) {
  if (!previousTier) {
    return false;
  }

  return !PaymentsUtils.isSameProduct(
    previousTier,
    previousDiscountType,
    currentTier,
    currentDiscountType,
  );
}

export function getGiftCardRemainingBalance(creditCardInfoCurrentAmount) {
  return creditCardInfoCurrentAmount == null || !creditCardInfoCurrentAmount.length
    ? -1
    : parseInt(creditCardInfoCurrentAmount, 10);
}
